import {
  Flex,
  Text,
  IconX,
  IconWarning,
  IconXOctagon,
  useBreakpoint,
  csx,
} from '@vtex/admin-ui'
import React from 'react'
import { FormattedMessage } from 'react-intl'

import { useCheckout } from '../../../../state'

enum MessageColor {
  Error = '#F8E3E3',
  Success = '#ECF3EC',
  Warning = '#FFEBD7',
  WarningBorder = '#F5AE70',
  ErrorBorder = '#F49494',
}

export const messagesIdMap: Record<string, string> = {
  withoutStock: 'checkout.cart.without-stock',
  withoutPriceFulfillment: 'checkout.cart.without-price-fullfilment',
  cannotBeDelivered: 'checkout.cart.cannot-be-delivered',
  nonExistentEan: 'checkout.cart.non-existent-ean',
}

export const CartMessage = () => {
  const { breakpoint } = useBreakpoint()
  const checkout = useCheckout()

  if (!checkout.checkout.cart?.messages?.length) {
    return null
  }

  const renderErrorMessages = () => {
    return (
      <div data-testid="cart-error-messages">
        {checkout.checkout.cart?.messages
          .filter((message) => message.status === 'error')
          .map((message) => (
            <div
              key={`cart-error-message-${message.text}`}
              className={csx({
                background: `${MessageColor.Error}`,
                borderRadius: 4,
                borderColor: `${MessageColor.ErrorBorder}`,
                padding: '$space-4',
                marginY: '$space-4',
                marginX: '$space-0',
                '@desktop': {
                  paddingX: '$space-7',
                },
                display: 'flex',
              })}
            >
              <div className={csx({ display: 'flex' })}>
                {breakpoint !== 'mobile' && (
                  <IconXOctagon
                    className={csx({
                      height: '0.70rem',
                      marginRight: '$space-4',
                    })}
                  />
                )}
                <Text>
                  {message.code ? (
                    <FormattedMessage
                      id={
                        messagesIdMap[message.code] ??
                        'checkout.cart.unmapped-problem'
                      }
                      values={{ itemName: message.fields.skuName }}
                    />
                  ) : (
                    <Text>{message.text}</Text>
                  )}
                </Text>
              </div>
            </div>
          ))}
      </div>
    )
  }

  const handleRemoveMessage = (text: string) => {
    checkout.removeMessageByText(text)
  }

  const renderWarningMessages = () => {
    return (
      <div data-testid="cart-warning-messages">
        {checkout.checkout.cart?.messages
          .filter((message) => message.status === 'warning')
          .map((message) => (
            <Flex
              key={`cart-warning-message-${message.text}`}
              justify="space-between"
              align="center"
              className={csx({
                background: `${MessageColor.Warning}`,
                borderRadius: 4,
                borderColor: `${MessageColor.WarningBorder}`,
                padding: '1rem',
                margin: '1rem 0rem 1rem 0rem',
                '@desktop': {
                  paddingX: '$space-7',
                },
              })}
            >
              <div className={csx({ display: 'flex' })}>
                {breakpoint !== 'mobile' && (
                  <IconWarning
                    className={csx({ height: '0.70rem', marginRight: '1rem' })}
                  />
                )}
                <Text>
                  {message.code && messagesIdMap[message.code] ? (
                    <FormattedMessage
                      id={messagesIdMap[message.code]}
                      values={{ itemName: message.fields.skuName }}
                    />
                  ) : (
                    <Text>{message.text}</Text>
                  )}
                </Text>
              </div>
              <IconX
                data-testid="remove-warning-message"
                className={csx({ height: '0.7rem', cursor: 'pointer' })}
                onClick={() => handleRemoveMessage(message.text)}
              />
            </Flex>
          ))}
      </div>
    )
  }

  return (
    <div data-testid="cart-messages">
      {renderErrorMessages()}
      {renderWarningMessages()}
    </div>
  )
}
